import { useMemo, useState } from 'react'
import type { Column, SortState } from './DataTable'

// Pure, stable sort by the active column's sortAccessor. null/undefined values
// sort last (in both directions); strings compare via localeCompare, everything
// else (number / boolean / Date) numerically. Returns the same array reference
// when sort is null or the column has no accessor (no-op).
export function sortRows<T>(rows: T[], columns: Column<T>[], sort: SortState | null): T[] {
  if (!sort) return rows
  const col = columns.find((c) => c.key === sort.key)
  if (!col?.sortAccessor) return rows
  const accessor = col.sortAccessor
  const dir = sort.dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = accessor(a)
    const vb = accessor(b)
    if (va == null && vb == null) return 0
    if (va == null) return 1
    if (vb == null) return -1
    const cmp = typeof va === 'string' && typeof vb === 'string'
      ? va.localeCompare(vb)
      : Number(va) - Number(vb)
    return cmp * dir
  })
}

// Stateful convenience for client-side tables: holds the sort, returns the
// sorted rows + the props DataTable needs. Caller sorts the WHOLE dataset with
// this, then paginates `sorted`, so sort composes correctly with pagination.
export function useTableSort<T>(
  rows: T[],
  columns: Column<T>[],
  opts?: { default?: SortState | null },
): { sorted: T[]; sort: SortState | null; onSortChange: (next: SortState | null) => void } {
  const [sort, setSort] = useState<SortState | null>(opts?.default ?? null)
  const sorted = useMemo(() => sortRows(rows, columns, sort), [rows, columns, sort])
  return { sorted, sort, onSortChange: setSort }
}
